import type {Evaluator, NodeParameters} from "@cm/graph-v3"
import {GraphNode} from "@cm/graph-v3"
import {skipped} from "#template/template-node/constants"
import type {PartlyVisited, PartlyVisitedResult, TemplateNodeContext} from "#template/template-node/types"

// The mapped result is structurally equivalent to `PartlyVisitedResult` by construction, but TS
// can't prove that through the runtime `instanceof` check — assert once at the boundary.
export function visitNone<ParamTypes extends NodeParameters>(parameters: ParamTypes): PartlyVisitedResult<ParamTypes> {
    const visitValue = (value: unknown): unknown => {
        if (value instanceof GraphNode) return skipped
        if (Array.isArray(value)) return value.map(visitValue)
        return value
    }
    const result: Record<string, unknown> = {}
    for (const key in parameters) result[key] = visitValue(parameters[key])
    return result as PartlyVisitedResult<ParamTypes>
}

export function visit<T>(node: T, evaluator: Evaluator<TemplateNodeContext>): PartlyVisited<T> {
    if (node instanceof GraphNode) return evaluator.getSync(node) as PartlyVisited<T>
    if (Array.isArray(node)) return node.map((v) => visit(v, evaluator)) as PartlyVisited<T>
    return node as PartlyVisited<T>
}

export function visitAll<ParamTypes extends NodeParameters>(
    parameters: ParamTypes,
    evaluator: Evaluator<TemplateNodeContext>,
): PartlyVisitedResult<ParamTypes> {
    const visitValue = (value: unknown): unknown => {
        if (value instanceof GraphNode) return evaluator.getSync(value)
        if (Array.isArray(value)) return value.map(visitValue)
        return value
    }
    const result: Record<string, unknown> = {}
    for (const key in parameters) result[key] = visitValue(parameters[key])
    return result as PartlyVisitedResult<ParamTypes>
}
